import React from 'react'
import { TableRecord, Button } from '@apex/component-library'
import { isEmpty, capitalize, isString } from 'lodash'
import { DividedTableData } from '.'
import { IconButton, Text } from '@chakra-ui/react'
import { IoClose } from 'react-icons/io5'
import { numberToCurrency, CurrencyEnum } from '../../utils/numberToCurrency'
import { getTransferDirection } from './getTransferDirection'
import {
  hasScheduleTransferUpdatePermissions,
  hasScheduleTransferCancelPermissions
} from '../../utils/permissionsHelper'
import { AuthorizationState } from '@Types'
import { SelectedTransfer } from './ScheduledTransfers'
import { ScheduledTransferResponse, TransferDirection, TransferStatus, TransferType } from '@Types'
import moment from 'moment'

type UpdateAmountButton = {
  amount: number
  direction: TransferDirection
  hasPermissionToUpdate: boolean
  id: string
  schedule: string
  status: TransferStatus
  type: TransferType
  onEditTransferAmount: (selectedTransfer: SelectedTransfer) => void
}

const getUpdateAmountButton = ({
  amount,
  direction,
  id,
  hasPermissionToUpdate,
  schedule,
  status,
  type,
  onEditTransferAmount
}: UpdateAmountButton) => {
  return hasPermissionToUpdate && status === 'ACTIVE'
    ? ((
        <Button
          size='sm'
          variant='link'
          padding='0'
          h='15px'
          fontWeight='400'
          onClick={() => onEditTransferAmount({ id, amount, direction, schedule, type })}
        >
          {numberToCurrency(amount, CurrencyEnum.usd)}
        </Button>
      ) as any)
    : `${numberToCurrency(amount, CurrencyEnum.usd)}`
}

export const getCancelButtonOrStatus = (
  status: TransferStatus,
  hasPermissionToCancel: boolean,
  transferItem: SelectedTransfer,
  onStartCancel: (selectedTransfer: SelectedTransfer) => void
) => {
  if (!hasPermissionToCancel && status === 'ACTIVE') {
    return ''
  }
  if (hasPermissionToCancel && status === 'ACTIVE') {
    return (
      <IconButton
        variant='ghost'
        fontSize='20px'
        h='15px'
        minW='none'
        border='none'
        size='sm'
        color='red'
        aria-label='Cancel Transfer'
        icon={<IoClose />}
        onClick={() => {
          onStartCancel(transferItem)
        }}
      />
    )
  }

  return capitalize(status)
}

export const transformTransfers = (
  scheduledTransfers: ScheduledTransferResponse[],
  auth: AuthorizationState,
  onEditTransferAmount: (selectedTransfer: SelectedTransfer) => void,
  onStartCancel: (selectedTransfer: SelectedTransfer) => void,
  accountNumber?: string
): DividedTableData => {
  const hasCancelPerm = hasScheduleTransferCancelPermissions(auth?.authorization?.permissions)
  const hasUpdatePerm = hasScheduleTransferUpdatePermissions(auth?.authorization?.permissions)

  if (isEmpty(scheduledTransfers)) {
    return { active: [], inActive: [] }
  }
  const tableRows = scheduledTransfers
    .sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf())
    .map((schedule) => {
      const {
        startDate,
        id,
        transferType,
        direction,
        frequencyInterval,
        frequencyUnit,
        occurrences,
        amount,
        status,
        iraDistributionDetails,
        iraContributionDetails,
        sourceAccount,
        destinationAccount
      } = schedule
      const transferTypeClean = transferType.replace('_', ' ')
      const transferDirection = getTransferDirection(
        direction,
        transferType,
        iraDistributionDetails,
        iraContributionDetails,
        accountNumber,
        sourceAccount,
        destinationAccount
      )
      const hasSpacer = transferDirection ? ' ' : ''
      const unitClean = frequencyUnit!.toLowerCase()
      const pluralOrNot = frequencyInterval! > 1 ? 's' : ''
      const scheduleDisplay = `${transferTypeClean}${hasSpacer}${transferDirection} every ${frequencyInterval} ${unitClean}${pluralOrNot}`
      const dateAsMilliseconds = moment(startDate).valueOf()
      return {
        key: id,
        date: dateAsMilliseconds,
        schedule: scheduleDisplay,
        occurrences: occurrences || 'Ongoing',
        amount: schedule.isFullDisbursement ? (
          <Text>Total</Text>
        ) : (
          getUpdateAmountButton({
            amount,
            direction,
            id,
            hasPermissionToUpdate: hasUpdatePerm,
            schedule: scheduleDisplay,
            status,
            type: transferType,
            onEditTransferAmount
          })
        ),
        status: getCancelButtonOrStatus(
          status,
          hasCancelPerm,
          { id, amount, schedule: '', direction, type: transferType },
          onStartCancel
        )
      } as TableRecord
    })

  const active = tableRows.filter((schedule) => !isString(schedule.status) || isEmpty(schedule.status))
  const inActive = tableRows.filter((schedule) => isString(schedule.status) && !isEmpty(schedule.status))
  const tableData: DividedTableData = { active, inActive }

  return tableData
}
